import { Base, createApp, importWhenVisible } from '@studiometa/js-toolkit';
import { Action, Menu } from '@studiometa/ui';
import {
  createApp as createDSApp,
  FontSizeControls,
  MenuItem,
  Navbar,
  Accordion,
  Tabs,
} from '@unistra/design-system';

import Figure from './atoms/Figure.js';
import FigureExternal from './atoms/FigureExternal.js';
import SelectLink from './atoms/SelectLink.js';
import DarkModeInput from './atoms/DarkModeInput.js';
import FakeSearchInput from './atoms/FakeSearchInput.js';
import DyslexiaInput from './atoms/DyslexiaInput.js';
import HighContrastModeInput from './atoms/HighContrastModeInput.js';
import TabSelect from './molecules/TabSelect.js';
import Search from './molecules/Search.js';
import TagsMore from './atoms/TagsMore.js';
import EntitiesMore from './atoms/EntitiesMore.js';
import AnchorNav from './molecules/AnchorNav.js';

import { isDev } from './config.js';

// RGAA 7.1 (24.4): the Webform module's help tooltip trigger uses
// role="tooltip" + aria-label, and tippy.js (via the module's own
// `aria: { content: 'auto' }` default) dynamically manages its own
// aria-describedby/aria-labelledby on that same trigger — which would fight
// with the fix applied by `WebformTooltipAccessibility.js` (lazy-loaded from
// `mounted()` below). Disable tippy's aria wiring through the module's own
// documented extension point instead of patching it. This must run before
// `Drupal.behaviors.webformElementHelpIcon.attach()` fires, so it's a
// top-level statement (executed as soon as this script runs) rather than
// something wired into a lifecycle hook. Guarded so it's a no-op on pages
// without any webform help trigger.
if (document.querySelector('.js-webform-element-help')) {
  window.Drupal = window.Drupal || {};
  window.Drupal.webform = window.Drupal.webform || {};
  window.Drupal.webform.elementHelpIcon = window.Drupal.webform.elementHelpIcon || {};
  window.Drupal.webform.elementHelpIcon.options = {
    aria: { content: false, expanded: null },
  };
}

createDSApp({
  components: [FontSizeControls, MenuItem, Navbar, Accordion, Tabs],
});

/**
 * Main App class.
 */
class App extends Base {
  /**
   * App config.
   * @returns {object}
   */
  static config = {
    log: isDev(),
    name: 'App',
    components: {
      Action,
      Figure,
      FigureExternal,
      SelectLink,
      FakeSearchInput,
      TabSelect,
      Search,
      AnchorNav,
      DarkModeInput,
      HighContrastModeInput,
      DyslexiaInput,
      Menu,
      Listing: (app) =>
        importWhenVisible(() => import('./organisms/Listing/index.js'), 'Listing', app),
      CalendarICS: (app) =>
        importWhenVisible(() => import('./atoms/CalendarICS.js'), 'CalendarICS', app),
      TagsMore,
      EntitiesMore,
      Map: (app) => importWhenVisible(() => import('./organisms/Map/index.js'), 'Map', app),
    },
  };

  /**
   * Log a nice message when app is ready.
   * @returns {void}
   */
  mounted() {
    this.handleOutsideLinks();

    // @note polyfill: toReversed for Array, avoid safari errors on some versions.
    if (![].toReversed) {
      Array.prototype.toReversed = function () {
        // eslint-disable-next-line no-var, vars-on-top
        for (var i = this.length - 1, arr = []; i >= 0; --i) {
          arr.push(this[i]);
        }

        return arr;
      };
    }

    this.handleCheckboxSubmitFocus();
    this.handleMenuAriaExpanded();
    this.handleWebformTooltipAccessibility();
  }

  /**
   * RGAA 7.1/8.9 (24.4): the Webform module's help "?" trigger fix is
   * specific to pages with webform elements, so it's lazy-loaded instead of
   * shipped/executed on every page.
   */
  handleWebformTooltipAccessibility() {
    if (!document.querySelector('.js-webform-element-help')) {
      return;
    }

    import('./molecules/WebformTooltipAccessibility.js').then(({ default: init }) => init());
  }

  /**
   * RGAA 7.1: the `Menu`/`MenuBtn`/`MenuList` components from `@studiometa/ui`
   * (used by "Ajouter à mon agenda" and any other dropdown built the same
   * way) never set `aria-expanded` on their trigger button. Rather than
   * patching the vendored package, mirror the `aria-hidden` state that
   * `MenuList` already toggles on itself (referenced by the button's own
   * `aria-controls`) onto the button's `aria-expanded`.
   *
   * `aria-controls` itself is set by `Menu`'s own `mounted()` hook, a
   * separate component whose mount order relative to this app isn't
   * guaranteed — so don't assume it's already there when this runs; wait
   * for it to appear if needed.
   */
  handleMenuAriaExpanded() {
    const wireButton = (btn) => {
      // aria-controls can be an arbitrary id, including ones starting with a
      // digit, which querySelector can't use directly as a CSS selector.
      // eslint-disable-next-line unicorn/prefer-query-selector
      const list = document.getElementById(btn.getAttribute('aria-controls'));
      if (!list) {
        return;
      }

      const sync = () => {
        btn.setAttribute(
          'aria-expanded',
          list.getAttribute('aria-hidden') === 'false' ? 'true' : 'false',
        );
      };

      sync();
      new MutationObserver(sync).observe(list, {
        attributes: true,
        attributeFilter: ['aria-hidden'],
      });
    };

    document.querySelectorAll('[data-component="MenuBtn"]').forEach((btn) => {
      if (btn.hasAttribute('aria-controls')) {
        wireButton(btn);
        return;
      }

      const waitForControls = new MutationObserver(() => {
        if (btn.hasAttribute('aria-controls')) {
          waitForControls.disconnect();
          wireButton(btn);
        }
      });
      waitForControls.observe(btn, { attributes: true, attributeFilter: ['aria-controls'] });
    });
  }

  /**
   * RGAA 12.8 (24.5/24.6): the "Afficher les informations"/"Modifier mes
   * réponses" webform checkboxes (enrollment form) reveal a new heading
   * ("Mes démarches de candidature" / "Formulaire") without moving focus to
   * it. Move focus there once the checkbox is checked, in addition to the
   * existing scroll-to-top behavior.
   */
  handleCheckboxSubmitFocus() {
    const revealedHeadingSelectors = {
      'edit-submit': '#edit-result-header h2',
      'edit-update-responses': '#edit-formulaire-legend',
    };

    document.querySelectorAll('.js-checkbox-submit').forEach((label) => {
      const forId = label.getAttribute('for');
      if (!forId) {
        return;
      }

      // this id can start with a digit, which querySelector can't use directly.
      // eslint-disable-next-line unicorn/prefer-query-selector
      const checkbox = document.getElementById(forId);
      if (!checkbox) {
        return;
      }

      // Listen on the checkbox's own `change` event, not a `click` on the
      // label: clicking the label does dispatch a click on the checkbox,
      // but pressing Space while the checkbox itself has focus (the normal
      // keyboard interaction) never dispatches a click on the label, so a
      // label-only listener misses exactly the keyboard users it's meant
      // to serve.
      checkbox.addEventListener('change', () => {
        window.scrollTo(0, 0);

        const revealedHeadingSelector = revealedHeadingSelectors[checkbox.id];
        if (!checkbox.checked || !revealedHeadingSelector) {
          return;
        }

        const focusRevealedHeading = () => {
          const revealedHeading = document.querySelector(revealedHeadingSelector);
          if (!revealedHeading) {
            return false;
          }

          if (!revealedHeading.hasAttribute('tabindex')) {
            revealedHeading.setAttribute('tabindex', '-1');
          }
          // preventScroll: the scrollTo(0, 0) above is the intended behavior
          // here; focus() scrolling the heading into view would immediately
          // undo it.
          revealedHeading.focus({ preventScroll: true });
          return true;
        };

        if (focusRevealedHeading()) {
          return;
        }

        // The heading may not be in the DOM yet: this holds for a Drupal
        // #states toggle (synchronous, covered by the immediate attempt
        // above), but not for a reveal driven by an AJAX response.
        const observer = new MutationObserver(() => {
          if (focusRevealedHeading()) {
            observer.disconnect();
          }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => observer.disconnect(), 10000);
      });
    });
  }

  /**
   * Handle different types of links and add appropriate icons
   */
  handleOutsideLinks() {
    const domains =
      typeof window.drupalSettings !== 'undefined' ? window.drupalSettings.data.domains : [];

    document.querySelectorAll('main a[href]').forEach((link) => {
      const href = link.href.toLowerCase();

      if (href.startsWith('tel:')) {
        link.classList.add('phone-link');
        link.setAttribute('rel', 'phone');
        return;
      }

      if (href.startsWith('mailto:')) {
        link.classList.add('email-link');
        link.setAttribute('rel', 'email');
        return;
      }

      if (href.includes('ernest.unistra.fr')) {
        link.classList.add('ernest-link');
        link.setAttribute('rel', 'ernest');
        return;
      }

      if (link.href.includes('#') || link.classList.contains('auto-anchor')) {
        return;
      }

      try {
        const linkUrl = new URL(link.href);
        const linkHostname = linkUrl.hostname;

        if (domains.length > 0) {
          const isInternal = domains.some((domain) => {
            return linkHostname === domain || linkHostname.endsWith(`.${domain}`);
          });

          if (!isInternal) {
            link.classList.add('external-link');
            link.setAttribute('rel', 'external');
          }
        }
      } catch {
        // Ignore malformed URLs
      }
    });
  }
}

export default createApp(App, document.body);
